import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Bgv from './Bgv';
import JoiningForm from './JoiningForm';
import OfferStatus from './OfferStatus';
import Document from './Document';
import { adminMenuItems } from '../../../../config/layout';
import PermissionGuard from '../../../../components/PermissionGuard';

const PreOnboarding = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [tabs, setTabs] = useState([]);

  // Filter out the document tab from tabs
  const filteredTabs = useMemo(() => tabs.filter(tab => tab.value !== 'document'), [tabs]);

  // Get tabs from layout configuration
  const myTeamItem = adminMenuItems.find(item => item.id === 'my-team');

  useEffect(() => {
    if (!tab) {
      setActiveTab(0);
      return;
    }

    const nextIndex = filteredTabs.findIndex((t) => t.value === tab);
    if (nextIndex !== -1) {
      setActiveTab(nextIndex);
    }
  }, [tab, filteredTabs]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(`/my-team/pre-onboarding/${filteredTabs[newValue].value}`);
  };

  // Get breadcrumbs - use active tab's breadcrumbs if available, otherwise use default
  const currentBreadcrumbs = useMemo(() => {
    const currentnavItem = myTeamItem?.subItems?.find((item) => item.id === "pre-onboarding");
    if (currentnavItem && currentnavItem?.tabs) {
      setTabs(currentnavItem?.tabs);
      if (activeTab >= 0 && filteredTabs[activeTab]?.breadcrumbs) {
        return filteredTabs[activeTab].breadcrumbs;
      }
    }
    return currentnavItem?.breadcrumbs
  }, [activeTab, filteredTabs]);


  return (
    <Container maxWidth="xl" sx={{ m: 0, p: 3 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: "1rem" }} />}
        sx={{
          marginBottom: 3,
          "& .MuiBreadcrumbs-li": {
            fontSize: "0.875rem",
          },
        }}
      >
        {currentBreadcrumbs.map((crumb, index) => (
          <Link
            key={crumb.path}
            underline={
              index === currentBreadcrumbs.length - 1 ? "none" : "hover"
            }
            color={
              index === currentBreadcrumbs.length - 1
                ? "inherit"
                : "inherit"
            }
            sx={{
              cursor:
                index === currentBreadcrumbs.length - 1
                  ? "default"
                  : "pointer",
              color:
                index === currentBreadcrumbs.length - 1
                  ? "#7522D1"
                  : "#4F4937",
              fontWeight:
                index === currentBreadcrumbs.length - 1 ? 600 : 400,
              "&:hover": {
                color:
                  index === currentBreadcrumbs.length - 1
                    ? "#7522D1"
                    : "#7522D1",
              },
            }}
            onClick={() =>
              index < currentBreadcrumbs.length - 1 &&
              navigate(crumb.path)
            }
          >
            {crumb.label}
          </Link>
        ))}
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#030007",
            marginBottom: 1,
          }}
        >
          Onboarding
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#4F4937",
            fontSize: "0.95rem",
            lineHeight: 1.6,
          }}
        >
          Manage pre-onboarding documents, background verification, offer status, and joining forms for new hires.
        </Typography>
      </Box>

      {/* Tabs Section */}
      <Box
        sx={{
          marginBottom: 3,
          display: "inline-flex",
          backgroundColor: "#F5EEFC",
          padding: "6px",
          borderRadius: "16px",
          gap: 0.5,
          border: "1px solid rgba(117, 34, 209, 0.08)",
        }}
      >
        {filteredTabs.map((tab, index) => (
          <PermissionGuard key={tab.value} module="onboarding" action="view">
            <Button
              onClick={() => handleTabChange(null, index)}
              sx={{
                textTransform: "none",
                fontSize: "0.9rem",
                fontWeight: activeTab === index ? 600 : 500,
                paddingX: 3,
                paddingY: 1,
                borderRadius: "12px",
                color: activeTab === index ? "#FFFFFF" : "#42374F",
                backgroundColor: activeTab === index ? "#7522D1" : "transparent",
                boxShadow: activeTab === index ? "0 4px 12px rgba(117, 34, 209, 0.25)" : "none",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: activeTab === index ? "#7522D1" : "rgba(117, 34, 209, 0.06)",
                  color: activeTab === index ? "#FFFFFF" : "#7522D1",
                  transform: activeTab === index ? "none" : "translateY(-1px)",
                },
              }}
            >
              {tab.label}
            </Button>
          </PermissionGuard>
        ))}
      </Box>

      {/* Tab Content */}
      <Box sx={{ paddingTop: 1 }}>
        {activeTab === 0 && (
          <PermissionGuard module="onboarding" action="view">
            <JoiningForm />
          </PermissionGuard>
        )}
        {activeTab === 1 && (
          <PermissionGuard module="onboarding" action="view">
            <Bgv />
          </PermissionGuard>
        )}
        {activeTab === 2 && (
          <PermissionGuard module="onboarding" action="view">
            <OfferStatus />
          </PermissionGuard>
        )}
      </Box>
    </Container>
  );
};

export default PreOnboarding;
